/**
 * Institutions Access Module
 *
 * Detects institutional IP access via REST API and redirects
 * to server-side validation endpoint to set cache segment.
 */

const CACHE_KEY = 'ns_institutions';
const CACHE_TIME_MS = 900000; // 15 minutes
const ACCESS_ENDPOINT = '/wp-json/ns-subscribers/v1/institutions-access';
const VALIDATION_PATH = '/institutions-access/';

/**
 * Check if the cache data is still valid.
 *
 * @param {string|null} cacheData The cached JSON string.
 * @return {boolean} True if cache is valid.
 */
function isValidCache ( cacheData ) {
	if ( ! cacheData ) {
		return false;
	}
	try {
		const cacheObj = JSON.parse( cacheData );
		return cacheObj.time && Date.now() < cacheObj.time;
	} catch {
		return false;
	}
}

/**
 * Set the cache item in sessionStorage.
 *
 * @param {boolean} redirect         Whether this was a redirect result.
 * @param {string}  organizationName The name of the organization.
 */
function setCacheItem ( redirect, organizationName = '' ) {
	const institutionName = organizationName ? `Open Athens: ${organizationName}` : '';
	window.sessionStorage.setItem(
		CACHE_KEY,
		JSON.stringify( {
			redirect: redirect.toString(),
			time: Date.now() + CACHE_TIME_MS,
			institutionName,
		} ),
	);
}

/**
 * Redirect to the validation endpoint with the current URL as return_url.
 *
 * window.location.href is encoded via encodeURIComponent which neutralises
 * any script content, and the server-side validate_return_url() method
 * further validates the URL before use.
 */
function redirectToValidation () {
	const returnUrl = encodeURIComponent( window.location.href ); // phpcs:ignore WordPressVIPMinimum.JS.Window.location -- encoded and server-validated.
	window.location.replace( VALIDATION_PATH + '?return_url=' + returnUrl ); // phpcs:ignore WordPressVIPMinimum.JS.Window.location -- redirect to same-origin validation endpoint.
}

/**
 * Initialize the institutions access check.
 */
export function init () {
	// Don't run on the validation page itself.
	if ( window.location.pathname.startsWith( VALIDATION_PATH ) ) { // phpcs:ignore WordPressVIPMinimum.JS.Window.location -- read-only comparison, not output.
		return;
	}

	// Check sessionStorage cache.
	const cached = window.sessionStorage.getItem( CACHE_KEY );
	if ( isValidCache( cached ) ) {
		return;
	}

	// Clear invalid cache.
	if ( cached ) {
		window.sessionStorage.removeItem( CACHE_KEY );
	}

	// Check REST API.
	fetch( ACCESS_ENDPOINT, { method: 'GET', credentials: 'same-origin' } )
		.then( response => {
			if ( response.status === 403 ) {
				// Not an institution IP.
				setCacheItem( false );
				return null;
			}
			if ( response.status === 200 ) {
				return response.json();
			}
			throw new Error( 'Error code ' + response.status );
		} )
		.then( data => {
			if ( data?.siteAccess === true ) {
				setCacheItem( true, data?.organisationName ?? '' );
				redirectToValidation();
			}
		} )
		.catch( error => {
			// eslint-disable-next-line no-console
			console.error( 'Institutions access check failed:', error );
		} );
}

// Auto-initialize when DOM is ready.
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}

